import React from 'react';
import PropTypes from 'prop-types';

import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import { t } from 'i18n/index';

// Buyer-facing SKU picker (SELL06). One select over the product's actual
// variant rows — the grid is sparse, so per-axis selects could compose
// combinations that don't exist. Sold-out SKUs stay visible but disabled.
const VariantSelector = ({ product, selectedVariantId, onChange }) => {
  const variants = product.variants || [];
  if (variants.length === 0) return null;

  // Name what the buyer is picking — "Choose size / colour" beats a generic
  // prompt, and its shape previews the value that replaces it ("M / Black").
  // Axis names are org-authored, so they read as typed in every locale; the
  // resource string interpolates them unescaped so the separator stays a slash.
  const axisNames = (product.options || []).map((axis) => axis.name).filter(Boolean);
  const placeholder = axisNames.length > 0
    ? t('literal:chooseOption', { options: axisNames.join(' / ') })
    : t('literal:selectOption');

  return (
    <FormControl fullWidth size="small" className="variant-selector">
      <Select
        displayEmpty
        // The menu renders in a portal, so it needs its own class to be reached
        // by the org-accent styling in mui-override.css.
        MenuProps={{ className: 'variant-selector-menu' }}
        inputProps={{ 'aria-label': placeholder }}
        value={selectedVariantId || ''}
        onChange={(event) => {
          const variant = variants.find((candidate) => candidate.id === event.target.value);
          onChange(variant || null);
        }}
        renderValue={(value) => {
          if (!value) return placeholder;
          const variant = variants.find((candidate) => candidate.id === value);
          return variant ? variant.label : placeholder;
        }}
      >
        {variants.map((variant) => (
          <MenuItem value={variant.id} key={variant.id} disabled={!variant.available}>
            {variant.label}
            {!variant.available && ` — ${t('literal:soldOut')}`}
            {product.show_supply && variant.available && variant.available_amount != null && variant.available_amount <= 5 &&
              ` (${variant.available_amount})`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

VariantSelector.propTypes = {
  product: PropTypes.object.isRequired,
  selectedVariantId: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

export default VariantSelector;
